import {
  ALERT_BANNER_ERROR_TYPE,
  ALERT_BANNER_INFO_TYPE,
  ALERT_BANNER_SUCCESS_TYPE,
  ALERT_BANNER_WARNING_TYPE,
  TOAST_DEFAULT_TIMEOUT,
} from '../../utils/spds/constants/feedback';
import { ALERT_MANAGER_EVENT_TYPE_BANNER } from '../../utils/spds/constants/alert-manager';
import Service, { service } from '@ember/service';

/**
 * SPDS Alert Banner Service
 *
 * Provides an API for displaying persistent alert banners.
 * Unlike toasts, banners remain visible until explicitly dismissed and can contain action buttons.
 * Supports both global banners (app-level) and local banners (container-scoped).
 *
 * @class SpdsAlertBannerService
 * @extends Service
 */
export default class SpdsAlertBannerService extends Service {
  @service alertManager;
  @service('spds/toast') toast;

  /**
   * Displays a global banner at the top of the application
   * @private
   * @param {Object} params - Banner configuration
   * @param {string} params.body - The body content of the banner
   * @param {string} [params.header] - The header content of the banner
   * @param {boolean} [params.includeIcon=true] - Whether to include a type icon
   * @param {Function} [params.onPrimaryBtnClick] - Primary button click handler
   * @param {Function} [params.onSecondaryBtnClick] - Secondary button click handler
   * @param {string} [params.primaryBtnText] - Primary button text
   * @param {string} [params.secondaryBtnText] - Secondary button text
   * @param {string} params.type - The banner type constant
   */
  #globalBanner = ({
    body,
    header,
    includeIcon = true,
    onPrimaryBtnClick,
    onSecondaryBtnClick,
    primaryBtnText,
    secondaryBtnText,
    type,
  }) => {
    this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_BANNER, {
      body,
      header,
      includeIcon,
      onPrimaryBtnClick,
      onSecondaryBtnClick,
      primaryBtnText,
      secondaryBtnText,
      type,
    });
  };

  /**
   * Displays a local banner within a specific container
   * @private
   * @param {Object} params - Banner configuration
   * @param {string} params.body - The body content of the banner
   * @param {string} [params.containerId='shared-layout-container'] - The ID of the container to display the banner
   * @param {string} [params.header] - The header content of the banner
   * @param {boolean} [params.includeIcon=true] - Whether to include a type icon
   * @param {Function} [params.onPrimaryBtnClick] - Primary button click handler
   * @param {Function} [params.onSecondaryBtnClick] - Secondary button click handler
   * @param {string} [params.primaryBtnText] - Primary button text
   * @param {string} [params.secondaryBtnText] - Secondary button text
   * @param {string} params.type - The banner type constant ('error', 'info', 'success', 'warning')
   */
  #localBanner = ({
    body,
    containerId = 'shared-layout-container',
    header,
    includeIcon = true,
    onPrimaryBtnClick,
    onSecondaryBtnClick,
    primaryBtnText,
    secondaryBtnText,
    type,
  }) => {
    this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_BANNER, {
      body,
      containerId,
      header,
      includeIcon,
      onPrimaryBtnClick,
      onSecondaryBtnClick,
      primaryBtnText,
      secondaryBtnText,
      type,
    });
  };

  /**
   * Extracts error messages from various error object formats
   * @private
   * @param {Object} error - An error object with title or detail properties
   * @returns {string|undefined} The extracted error message
   */
  #getErrorMessages = error => {
    if (error.title) return error.title || error.title.toString();
    if (error.detail) return error.detail.error || error.detail.toString();
  };

  /**
   * Parses error objects into an array of error messages
   * Handles multiple error formats including simple errors, nested errors, and API error responses.
   * @private
   * @param {Error|string|Object|null|undefined} error - The error to parse
   * @returns {string[]} Array of error messages
   */
  #parseDynamicErrors = error => {
    if (!error) return [];

    let messages = [error.message || error];

    if (error.errors?.length) {
      messages = error.errors.map(this.#getErrorMessages).filter(Boolean);
    }

    return messages;
  };

  /**
   * Clears all global banners
   * @returns {Promise<*>} Result from the clear operation
   *
   * @example
   * await this.alertBanner.clearGlobal();
   */
  clearGlobal = () => this.alertManager.clear('banner');

  /**
   * Clears all banners in a specific container
   * @param {string} id - The container ID to clear banners from
   * @returns {Promise<*>} Result from the clear operation
   *
   * @example
   * await this.alertBanner.clearLocal('my-form-container');
   */
  clearLocal = id => this.alertManager.clear('banner', id);

  /**
   * Intelligently displays errors as either banners (with details) or toasts (simple messages)
   * If the error contains detailed messages, displays as a banner with a list.
   * If the error is null/undefined or simple, displays as a toast.
   *
   * @param {Object} params - Error display parameters
   * @param {boolean} [params.isLocal] - If true, shows local toast; otherwise global toast
   * @param {string} params.title - The title/header of the error message
   * @param {Error|string|Object|null|undefined} [params.error] - Error object, string, or API error response.
   *   If null/undefined, displays as toast. If contains details, displays as banner.
   * @param {string} [params.containerId='shared-layout-container'] - Container ID for banners
   * @param {number} [params.timeout=3000] - Toast auto-dismiss duration in milliseconds
   *
   * @example
   * // Simple error - shows toast
   * this.alertBanner.dynamicError({
   *   title: 'Failed to save',
   *   error: null
   * });
   *
   * @example
   * // API error with details - shows banner
   * this.alertBanner.dynamicError({
   *   title: 'Validation Failed',
   *   error: {
   *     errors: [
   *       { title: 'Name is required' },
   *       { detail: 'Email is invalid' }
   *     ]
   *   },
   *   containerId: 'form-container'
   * });
   */
  dynamicError({
    isLocal,
    title,
    error,
    containerId = 'shared-layout-container',
    timeout = TOAST_DEFAULT_TIMEOUT,
  }) {
    let messages = this.#parseDynamicErrors(error);

    if (messages?.length) {
      this.localError({
        header: title,
        body: `<ol><li>${messages.join(`.</li><li>`)}</li></ol>`,
        containerId,
      });
    } else if (isLocal) {
      this.toast.localError(title, timeout);
    } else {
      this.toast.error(title, timeout);
    }
  }

  /**
   * Displays a global error banner
   * @param {Object} options - Banner configuration (see #globalBanner for details)
   * @example
   * this.alertBanner.globalError({
   *   header: 'Error',
   *   body: 'Something went wrong'
   * });
   */
  globalError = options => this.#globalBanner({ ...options, type: ALERT_BANNER_ERROR_TYPE });

  /**
   * Displays a global informational banner
   * @param {Object} options - Banner configuration (see #globalBanner for details)
   * @example
   * this.alertBanner.globalInfo({
   *   header: 'Notice',
   *   body: 'System maintenance scheduled'
   * });
   */
  globalInfo = options => this.#globalBanner({ ...options, type: ALERT_BANNER_INFO_TYPE });

  /**
   * Displays a global success banner
   * @param {Object} options - Banner configuration (see #globalBanner for details)
   * @example
   * this.alertBanner.globalSuccess({
   *   header: 'Success',
   *   body: 'Operation completed'
   * });
   */
  globalSuccess = options => this.#globalBanner({ ...options, type: ALERT_BANNER_SUCCESS_TYPE });

  /**
   * Displays a global warning banner
   * @param {Object} options - Banner configuration (see #globalBanner for details)
   * @example
   * this.alertBanner.globalWarning({
   *   header: 'Warning',
   *   body: 'Please review your settings'
   * });
   */
  globalWarning = options => this.#globalBanner({ ...options, type: ALERT_BANNER_WARNING_TYPE });

  /**
   * Displays a local error banner in a specific container
   * @param {Object} options - Banner configuration (see #localBanner for details)
   * @example
   * this.alertBanner.localError({
   *   header: 'Validation Error',
   *   body: 'Please fix the errors',
   *   containerId: 'form-container'
   * });
   */
  localError = options => this.#localBanner({ ...options, type: ALERT_BANNER_ERROR_TYPE });

  /**
   * Displays a local informational banner in a specific container
   * @param {Object} options - Banner configuration (see #localBanner for details)
   * @example
   * this.alertBanner.localInfo({
   *   body: 'Additional information available',
   *   containerId: 'sidebar'
   * });
   */
  localInfo = options => this.#localBanner({ ...options, type: ALERT_BANNER_INFO_TYPE });

  /**
   * Displays a local success banner in a specific container
   * @param {Object} options - Banner configuration (see #localBanner for details)
   * @example
   * this.alertBanner.localSuccess({
   *   body: 'Changes saved',
   *   containerId: 'settings-panel'
   * });
   */
  localSuccess = options => this.#localBanner({ ...options, type: ALERT_BANNER_SUCCESS_TYPE });

  /**
   * Displays a local warning banner in a specific container
   * @param {Object} options - Banner configuration (see #localBanner for details)
   * @example
   * this.alertBanner.localWarning({
   *   header: 'Unsaved Changes',
   *   body: 'Your changes will be lost',
   *   containerId: 'editor'
   * });
   */
  localWarning = options => this.#localBanner({ ...options, type: ALERT_BANNER_WARNING_TYPE });
}
